import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '@/config/app';
import { AshlarLogo } from '@/components/AshlarLogo';
import { Input } from '@/components/ui/input';
import { Container } from '@/components/g5';
import { AuraButton } from '@/components/aura';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { TurnstileWidget } from '@/components/TurnstileWidget';

const emailSchema = z.string().trim().email().max(255);
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

// Footer link groups — each column stays ≤6 entries so the footer keeps a
// bounded height as the lander catalog grows. The full catalog lives on
// /find-an-appraiser; the footer carries only the front doors.
const homeownerLinks = [
  { label: 'Find an Appraiser', href: '/find-an-appraiser' },
  { label: 'Estate Appraisals', href: '/estate-appraisal' },
  { label: 'Divorce Appraisals', href: '/divorce-appraisal' },
  { label: 'Property Tax Appeals', href: '/property-tax-appeal' },
  { label: 'Pre-Listing Appraisals', href: '/pre-listing-appraisal' },
  { label: 'All Appraisal Services', href: '/find-an-appraiser' },
];
const appraiserLinks = [
  { label: 'For Appraisers', href: '/for-appraisers' },
  { label: 'Get Found', href: '/get-found' },
  { label: 'Features', href: '/features' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Login', href: '/login' },
  { label: 'Sign Up', href: '/login?tab=signup' },
];
const resourceLinks = [
  { label: 'Articles', href: '/articles' },
  { label: 'Glossary', href: '/glossary' },
  { label: 'Resources', href: '/resources' },
  { label: 'News', href: '/news' },
  { label: 'About', href: '/about' },
];
const legalLinks = [
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy' },
];

export function LandingFooter() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', { body: { email: result.data, turnstileToken } });
      if (error) throw error;
      if (data?.message === 'already_subscribed') toast.info("You're already subscribed!");
      else { toast.success('Check your inbox to confirm your subscription!'); setEmail(''); }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const linkCls = 'text-sm transition-colors text-[color:var(--g5-sub)] hover:text-[color:var(--g5-ink)]';
  const colHead = 'uppercase';
  const colHeadStyle = { fontFamily: MONO, fontSize: '0.6rem', letterSpacing: '0.16em', color: 'var(--g5-accent)' } as const;

  return (
    <footer style={{ background: 'var(--g5-soft)', borderTop: '1px solid var(--g5-line)' }}>
      <Container className="py-12 md:py-16">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
          {/* Brand + newsletter */}
          <div className="col-span-2 space-y-4">
            <AshlarLogo className="h-10 w-auto" />
            <p className="max-w-md text-sm" style={{ color: 'var(--g5-sub)' }}>
              {APP_CONFIG.tagline}. Built and maintained by a licensed New Jersey Certified General
              appraiser. Serving New Jersey first, expanding nationwide.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="mt-4 flex max-w-sm flex-col gap-2">
              <div className="flex gap-2">
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <Input
                  id="newsletter-email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 flex-1 rounded-full px-4 text-sm"
                  style={{ background: 'var(--g5-card)', borderColor: 'var(--g5-line)', color: 'var(--g5-ink)' }}
                  maxLength={255}
                  aria-label="Email address for newsletter"
                />
                <AuraButton type="submit" size="sm" disabled={loading || !turnstileToken}>
                  Subscribe
                </AuraButton>
              </div>
              <TurnstileWidget onVerify={setTurnstileToken} />
            </form>
            <p style={{ fontFamily: MONO, fontSize: '0.62rem', color: 'var(--g5-sub)' }}>Stay updated on platform launches and industry news.</p>
          </div>

          <div>
            <p className={colHead} style={colHeadStyle}>For homeowners</p>
            <ul className="mt-3 space-y-2">
              {homeownerLinks.map((link) => (
                <li key={link.label}><button onClick={() => navigate(link.href)} className={linkCls}>{link.label}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <p className={colHead} style={colHeadStyle}>For appraisers</p>
            <ul className="mt-3 space-y-2">
              {appraiserLinks.map((link) => (
                <li key={link.href}><button onClick={() => navigate(link.href)} className={linkCls}>{link.label}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <p className={colHead} style={colHeadStyle}>Resources</p>
            <ul className="mt-3 space-y-2">
              {resourceLinks.map((link) => (
                <li key={link.href}><button onClick={() => navigate(link.href)} className={linkCls}>{link.label}</button></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t pt-8 md:flex-row" style={{ borderColor: 'var(--g5-line)' }}>
          <p style={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.08em', color: 'var(--g5-sub)' }}>© {currentYear} {APP_CONFIG.name}. All rights reserved.</p>
          <div className="flex gap-6">
            {legalLinks.map((link) => (
              <button key={link.href} onClick={() => navigate(link.href)} className={linkCls}>{link.label}</button>
            ))}
          </div>
        </div>
      </Container>
    </footer>
  );
}
